import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, Users, TrendingUp, Sparkles, Heart, Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import api, { formatXOF } from "../lib/api";
import CagnotteCard from "../components/CagnotteCard";
import { useAuth } from "../contexts/AuthContext";

export default function Landing() {
  const { user } = useAuth();
  const [popular, setPopular] = useState([]);
  const [recent, setRecent] = useState([]);
  const [participated, setParticipated] = useState([]);

  useEffect(() => {
    api.get("/cagnottes/explore?sort=popular&limit=6").then(({ data }) => setPopular(data)).catch(() => {});
    api.get("/cagnottes/explore?sort=recent&limit=6").then(({ data }) => setRecent(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/cagnottes/participated").then(({ data }) => setParticipated(data)).catch(() => {});
    } else {
      setParticipated([]);
    }
  }, [user]);

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay" />
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-amber-300/20 dark:bg-amber-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-emerald-300/20 dark:bg-emerald-600/10 blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-40 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass mb-6 text-xs font-medium" data-testid="hero-badge">
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                <span>Paiements Wave & Orange Money</span>
              </div>
              <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
                Collectez des fonds <br />
                avec <span className="gradient-text">élégance</span>.
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
                Sen Cagnotte est la plateforme premium de cagnottes en ligne, conçue pour
                le Sénégal et l'Afrique francophone. Créez, partagez, collectez — en quelques minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/register">
                  <Button size="lg" className="btn-gradient rounded-full text-base px-8 h-12" data-testid="hero-cta-register">
                    Créer ma cagnotte <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/explore">
                  <Button size="lg" variant="outline" className="rounded-full text-base px-8 h-12 border-2" data-testid="hero-cta-explore">
                    Explorer les cagnottes
                  </Button>
                </Link>
              </div>

              <div className="flex items-center gap-6 mt-10 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> 100% sécurisé</div>
                <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Mise en ligne en 2min</div>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden card-soft animate-fade-up" style={{ animationDelay: "0.2s" }}>
                <img
                  src="https://images.pexels.com/photos/5466267/pexels-photo-5466267.jpeg"
                  alt="Sen Cagnotte"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 glass-strong rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-semibold text-foreground">EN COURS</span>
                  </div>
                  <h3 className="font-display font-bold text-lg">Mariage de Aïssatou</h3>
                  <div className="mt-2 h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full gradient-gold-green w-3/4 rounded-full" />
                  </div>
                  <div className="flex justify-between mt-2 text-xs">
                    <span className="font-semibold gradient-text">{formatXOF(750000)}</span>
                    <span className="text-muted-foreground">{formatXOF(1000000)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <span className="text-xs font-bold tracking-widest text-amber-600 dark:text-amber-400 uppercase">Pourquoi Sen Cagnotte</span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3">
              Conçu pour ceux qui veulent <span className="gradient-text">aller plus loin</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: "Paiements sécurisés", desc: "Wave & Orange Money. Paiement instantané via mobile money, vérifié en temps réel." },
              { icon: TrendingUp, title: "Suivi en temps réel", desc: "Visualisez vos contributions, exportez la liste, partagez votre QR code instantanément." },
              { icon: Users, title: "Public ou privé", desc: "Choisissez la visibilité de votre cagnotte. Lien partageable sur WhatsApp en un clic." },
            ].map((f, i) => (
              <div key={i} className="card-soft p-8 group" data-testid={`feature-${i}`}>
                <div className="h-12 w-12 rounded-xl gradient-gold-green flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <f.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-display font-semibold text-xl mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Participated cagnottes (logged-in only) */}
      {user && participated.length > 0 && (
        <section className="py-16 lg:py-20 bg-gradient-to-br from-emerald-50/40 to-amber-50/40 dark:from-emerald-950/20 dark:to-amber-950/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <span className="text-xs font-bold tracking-widest text-amber-600 dark:text-amber-400 uppercase flex items-center gap-2">
                  <Gift className="h-4 w-4" /> Vos contributions
                </span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Cagnottes auxquelles vous avez participé</h2>
                <p className="text-sm text-muted-foreground mt-2">Suivez l'évolution des cagnottes que vous soutenez.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {participated.slice(0, 6).map((c) => <CagnotteCard key={c.id} cagnotte={c} />)}
            </div>
          </div>
        </section>
      )}

      {/* Popular cagnottes */}
      {popular.length > 0 && (
        <section className="py-20 lg:py-28 bg-secondary/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <span className="text-xs font-bold tracking-widest text-amber-600 dark:text-amber-400 uppercase">Tendances</span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Cagnottes populaires</h2>
              </div>
              <Link to="/explore?sort=popular">
                <Button variant="outline" className="rounded-full" data-testid="see-all-popular">
                  Tout voir <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popular.slice(0, 6).map((c) => <CagnotteCard key={c.id} cagnotte={c} />)}
            </div>
          </div>
        </section>
      )}

      {/* Recent cagnottes */}
      {recent.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <span className="text-xs font-bold tracking-widest text-amber-600 dark:text-amber-400 uppercase">Nouveautés</span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Cagnottes récentes</h2>
              </div>
              <Link to="/explore">
                <Button variant="outline" className="rounded-full" data-testid="see-all-recent">
                  Tout voir <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recent.slice(0, 6).map((c) => <CagnotteCard key={c.id} cagnotte={c} />)}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl gradient-gold-green p-12 lg:p-20 text-center text-white">
            <div className="absolute inset-0 grain-overlay opacity-30" />
            <Heart className="h-12 w-12 mx-auto mb-6 opacity-90" />
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-4">
              Prêt·e à transformer une idée en réalité ?
            </h2>
            <p className="text-lg opacity-90 max-w-2xl mx-auto mb-8">
              Anniversaire, mariage, projet associatif, urgence familiale — créez votre cagnotte en 2 minutes.
            </p>
            <Link to="/register">
              <Button size="lg" className="rounded-full bg-white text-emerald-700 hover:bg-white/90 hover:scale-105 transition-all text-base px-10 h-12" data-testid="cta-final-register">
                Lancer ma cagnotte <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
